// KB-2103 — first-class `priceState` for offers: priced / free / unknown.
//
// THE DEFECT THIS EXISTS TO FIX: an offer whose price was never published and
// an offer that is genuinely free both store `0`, so nothing downstream can
// tell them apart. Today that renders as a BLANK price (not `$0` — every
// render site is gated on a non-empty string from the app's PriceDisplayHelper),
// and a blank is useless to a shopper. Worse, `priceRegular: 0` still satisfies
// a `$0–$50` price-range filter, so an unpriced camp silently matches a budget
// the operator cannot be shown to meet.
//
// The fix is to stop overloading `price == 0` and name the three states. KB-2095
// untangled exactly this ambiguity on the WRITE side (published `$0` + an
// observed price → auto-fill); overloading the same `0` again on the READ side
// would repeat the mistake it just corrected.
//
// THE RULE (this module is the single source of truth for it). `priceState` is
// the definitive semantic, and the NUMBERS ARE NORMALISED TO AGREE with it —
// see normalizePriceNumbersForState below, which is what makes the search
// filters correct without any exclusion clause:
//
//   state       priceState   priceCalc / priceRegular
//   ---------   ----------   ------------------------
//   priced      null         the real value
//   free        "free"       0     (a free camp is within every budget)
//   unknown     "unknown"    null  (answers no price question at all)
//
//   a real price exists          → null      ("priced" — the numbers always win)
//   no price, curated as free    → "free"    (a human claim about the world)
//   no price                     → "unknown" (an absence of data)
//
// WHY THE NUMBERS ALWAYS WIN: a curated `free` on an offer that later acquires a
// real $300 price would otherwise keep rendering "Free". Curation is honoured
// only where it is meaningful — when the price is genuinely absent.
//
// WHY *FREE* IS NEVER DERIVED: "this camp costs nothing" is a claim about the
// world; "we have no price" is an absence of data. Only the second is safely
// inferable. Camp Riverlea's Counselors-In-Training programme (`o_cit`,
// `/volunteer-orc/`) is the real $0 that must never be flattened into unknown —
// and it earns "free" by curation, not by inference.
//
// WHY NO `published` GATE: the publish state mattered for KB-2095's *alarm*,
// which counts customer-visible damage. It does not belong in the deriver — an
// offer with no price has no price whether or not it is published, and search
// already excludes unpublished offers upstream via `visibility:published`.
// Keeping the gate out means no visibility plumbing through every write path.
//
// MIRRORED IN THE APP: kb-ff's `PriceDisplayHelper.priceStateForOffer`
// (lib/utils/price_display.dart) implements this identical truth table, so the
// DISPLAY resolves correctly from the numbers alone even for an offer this
// deriver has never re-saved. The app deliberately does NOT mirror the filter
// side: search correctness comes from the normalised numbers (an unknown offer
// has no price attribute in Algolia at all), not from a clause the client has
// to remember to append. Both sides are pinned by tests over the same cases; if
// you change the rule here, change it there in the same PR.

export type PriceState = "unknown" | "free";

/**
 * Below this, a stored number is not a real camp price.
 *
 * KB-2103 folds in a pre-existing FOURTH pseudo-state: the app already rendered
 * any price under $10 as "Ask provider" (price_display.dart:52) — an
 * undocumented heuristic standing in for "this number isn't a real price",
 * which is precisely the state this module makes explicit. Two mechanisms for
 * one idea is how the original `0` ambiguity arose, so the heuristic is folded
 * in here rather than kept alongside.
 *
 * The threshold is deliberately shared with the app's mirror of this rule; if
 * you move it, move it in both places or the filters and the display will
 * disagree about the same offer.
 */
export const MIN_REAL_PRICE = 10;

/** A price is "real" only when present, finite, and a plausible camp price. */
function hasRealPrice(value: unknown): boolean {
  return typeof value === "number" && Number.isFinite(value) && value >= MIN_REAL_PRICE;
}

/**
 * Derive an offer's price state, or `null` when the offer is normally priced.
 *
 * Pure: no Firestore, no clock. `curatedPriceState` is whatever is already
 * stored on the offer doc — only `"free"` is honoured, and only when no
 * positive price is present.
 */
export function computePriceState(
  offer:
    | {
        priceCalc?: unknown;
        priceRegular?: unknown;
        currentSalePrice?: unknown;
        priceState?: unknown;
      }
    | null
    | undefined,
): PriceState | null {
  if (
    hasRealPrice(offer?.priceCalc) ||
    hasRealPrice(offer?.priceRegular) ||
    hasRealPrice(offer?.currentSalePrice)
  ) {
    return null;
  }
  return offer?.priceState === "free" ? "free" : "unknown";
}

/**
 * Normalise an offer's price NUMBERS so they agree with its price STATE.
 *
 *   priced   → untouched (a real price is the price)
 *   free     → 0     — a free camp genuinely costs zero, so it should SATISFY a
 *                      budget filter. Storing null would hide it from every
 *                      price-filtered search, which is wrong: $0 is within
 *                      every budget.
 *   unknown  → null  — an offer with no price must not answer a price question
 *                      at all.
 *
 * WHY THIS IS THE WHOLE MECHANISM. The Algolia sync extension
 * (`algolia/firestore-algolia-search`, v1.2.10 in prod) DROPS null-valued keys
 * from the record it uploads — `isValidValue` in its `functions/src/util.ts`
 * returns `value !== null`, and `processObject` only assigns keys that pass it.
 * So `priceCalc: null` in Firestore means the Algolia record has NO `priceCalc`
 * attribute, and an Algolia NUMERIC filter never matches a record missing its
 * attribute. Unknown-price offers therefore drop out of every price-range and
 * price-tier filter by construction — no exclusion clause, no facet, no
 * backfill-before-deploy ordering hazard.
 *
 * This is the same mechanism KB-1412 already relies on for `priceBucket`
 * (see prepareOfferDataCore's bucketing comment: "Offers with no price get a
 * null bucket and are absent from the facet"). It is existing, load-bearing
 * behaviour of this pipeline, not a new assumption.
 *
 * Note the asymmetry that makes `NOT priceState:unknown` the WRONG tool: for a
 * `NOT attr:value` clause Algolia matches records that LACK the attribute, so
 * such a clause silently retains every offer that has not been re-saved. The
 * numeric rule is the inverse, and it is the one that fails safe.
 *
 * Pure: no Firestore sentinels. Callers write these values through their normal
 * write path; `null` is a written value, not a deletion, and needs no
 * `FieldValue.delete()`.
 */
export function normalizePriceNumbersForState(
  state: PriceState | null,
  prices: {
    priceCalc: number | null | undefined;
    priceRegular: number | null | undefined;
    /**
     * KB-2782 AC-5 — optional so every pre-existing caller (this module's own
     * tests included) is byte-unchanged when it doesn't pass tier prices at
     * all. Passed by prepareOfferDataCore so priceTier2Calc/priceTier3Calc
     * stop being the one price-shaped pair that skips this normalisation.
     */
    priceTier2Calc?: number | null | undefined;
    priceTier3Calc?: number | null | undefined;
  },
): {
  priceCalc: number | null;
  priceRegular: number | null;
  priceTier2Calc: number | null;
  priceTier3Calc: number | null;
} {
  if (state === null) {
    return {
      priceCalc: prices.priceCalc ?? null,
      priceRegular: prices.priceRegular ?? null,
      priceTier2Calc: prices.priceTier2Calc ?? null,
      priceTier3Calc: prices.priceTier3Calc ?? null,
    };
  }
  const normalized = state === "free" ? 0 : null;
  // A tier price is a discount OFF the public price. When the public price itself isn't real —
  // free (nothing to discount from) or unknown (no price question answered at all) — the tier
  // claim isn't real either. Tiers null out in BOTH states, unlike priceCalc/priceRegular (which
  // follow the free→0 rule so a free camp still satisfies a budget filter): there is no "free
  // tier 2" concept to preserve the way there is a free public price.
  return { priceCalc: normalized, priceRegular: normalized, priceTier2Calc: null, priceTier3Calc: null };
}
